import { ApproximateLocation, parseApproximateLocation } from './approximate-location';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const CLINIC_TIMEZONE = 'Europe/Stockholm';

export const EVIDENSIA_BOOKING_URL = 'https://app.evidensia.se/booking-login?clinicId=0';
export const EVIDENSIA_ID_MARKING_URL = 'https://evidensia.se/vara-tjanster/id-markning/';
// The ID-marking page uses page ID 4468 to restrict the result to relevant clinics.
export const EVIDENSIA_CLINICS_URL = 'https://evidensia.se/api/clinics/getall/4468/';
export const EVIDENSIA_TREATMENTS = {
  'id-marking': { url: EVIDENSIA_ID_MARKING_URL, clinicsUrl: EVIDENSIA_CLINICS_URL },
  'vaccination-dog': {
    url: 'https://evidensia.se/vara-tjanster/vaccination/',
    clinicsUrl: 'https://evidensia.se/api/clinics/getall/4473/',
  },
  'vaccination-cat': {
    url: 'https://evidensia.se/vara-tjanster/vaccination-katt/',
    clinicsUrl: 'https://evidensia.se/api/clinics/getall/4472/',
  },
} as const;
export type EvidensiaTreatment = keyof typeof EVIDENSIA_TREATMENTS;

export function isEvidensiaTreatment(value: unknown): value is EvidensiaTreatment {
  return typeof value === 'string' && Object.hasOwn(EVIDENSIA_TREATMENTS, value);
}

export type EvidensiaClinic = ApproximateLocation & {
  id: number;
  name: string;
  address: string;
  city: string;
  imageUrl: string | null;
  url: string;
  bookingUrl: string | null;
  phone: string | null;
  phoneInfo: string | null;
  openingHours: {
    date: string;
    opensAt: string | null;
    closesAt: string | null;
    allDay: boolean;
    closed: boolean;
  } | null;
};

function clinicText(value: unknown): string {
  return typeof value === 'string'
    ? value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|\u00a0/gi, ' ')
        .replace(/&shy;|\u00ad/gi, '')
        .replace(/\s+/g, ' ')
        .trim()
    : '';
}

function clinicDate(value: unknown): string | null {
  if (
    typeof value !== 'string' ||
    !/^20\d{2}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value)
  )
    return null;
  const date = fromZonedTime(value, CLINIC_TIMEZONE);
  return !Number.isNaN(date.getTime()) &&
    formatInTimeZone(date, CLINIC_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ss") === value
    ? date.toISOString()
    : null;
}

function parseOpeningHours(
  clinic: Record<string, unknown>,
  now: Date,
): EvidensiaClinic['openingHours'] {
  const today = clinicText(clinic.opening_hours_today).replace(/^Idag:\s*/i, '');
  const date = formatInTimeZone(now, CLINIC_TIMEZONE, 'yyyy-MM-dd');
  if (/^Stängt$/i.test(today))
    return { date, opensAt: null, closesAt: null, allDay: false, closed: true };
  if (
    clinic.opening_hours_open_today !== false &&
    (clinic.open_24_hours === true || /^Öppet dygnet runt$/i.test(today))
  )
    return { date, opensAt: null, closesAt: null, allDay: true, closed: false };

  const opensAt = clinicDate(clinic.opening_hours_startdate);
  let closesAt = clinicDate(clinic.opening_hours_enddate);
  // Evidensia represents closing at midnight as 00:00 on the opening date.
  if (
    opensAt &&
    closesAt &&
    closesAt <= opensAt &&
    typeof clinic.opening_hours_enddate === 'string' &&
    clinic.opening_hours_enddate.endsWith('T00:00:00') &&
    /\d{2}:\d{2}\s*[-–]\s*00:00$/.test(today)
  ) {
    const nextDay = new Date(`${clinic.opening_hours_enddate.slice(0, 10)}T12:00:00Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    closesAt = clinicDate(`${nextDay.toISOString().slice(0, 10)}T00:00:00`);
  }
  if (
    !opensAt ||
    !closesAt ||
    new Date(closesAt).getTime() <= new Date(opensAt).getTime() ||
    new Date(closesAt).getTime() - new Date(opensAt).getTime() > 24 * 60 * 60 * 1000
  )
    return null;
  return {
    date: formatInTimeZone(opensAt, CLINIC_TIMEZONE, 'yyyy-MM-dd'),
    opensAt,
    closesAt,
    allDay: false,
    closed: false,
  };
}

export function getEvidensiaOpeningStatus(
  clinic: EvidensiaClinic,
  now: Date,
): 'open' | 'closed' | null {
  const hours = clinic.openingHours;
  if (!hours || Number.isNaN(now.getTime())) return null;
  if (hours.opensAt && hours.closesAt) {
    if (now >= new Date(hours.opensAt) && now < new Date(hours.closesAt)) return 'open';
  }
  // Today's data must not become tomorrow's opening hours after midnight.
  if (hours.date !== formatInTimeZone(now, CLINIC_TIMEZONE, 'yyyy-MM-dd')) return null;
  if (hours.closed) return 'closed';
  if (hours.allDay) return 'open';
  return hours.opensAt && hours.closesAt ? 'closed' : null;
}

export function getEvidensiaOpeningTimes(clinic: EvidensiaClinic) {
  const hours = clinic.openingHours;
  if (!hours?.opensAt || !hours.closesAt) return null;
  const closes = formatInTimeZone(hours.closesAt, CLINIC_TIMEZONE, 'HH:mm');
  return {
    opens: formatInTimeZone(hours.opensAt, CLINIC_TIMEZONE, 'HH:mm'),
    closes:
      closes === '00:00' &&
      formatInTimeZone(hours.closesAt, CLINIC_TIMEZONE, 'yyyy-MM-dd') !== hours.date
        ? '24:00'
        : closes,
  };
}

function evidensiaUrl(value: unknown, hostname: string, baseUrl?: string): string | null {
  if (typeof value !== 'string') return null;

  try {
    const url = new URL(value, baseUrl);
    return url.protocol === 'https:' &&
      url.hostname === hostname &&
      !url.port &&
      !url.username &&
      !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function parseEvidensiaClinics(value: unknown, now = new Date()): EvidensiaClinic[] | null {
  if (
    !value ||
    typeof value !== 'object' ||
    !('clinics' in value) ||
    !Array.isArray(value.clinics)
  ) {
    return null;
  }

  const clinics: EvidensiaClinic[] = [];

  for (const entry of value.clinics) {
    if (!entry || typeof entry !== 'object') continue;

    const clinic = entry as Record<string, unknown>;
    const location = parseApproximateLocation(clinic);
    const url = evidensiaUrl(clinic.permalink, 'evidensia.se');

    if (
      !location ||
      !url ||
      typeof clinic.id !== 'number' ||
      !Number.isInteger(clinic.id) ||
      clinic.id <= 0 ||
      typeof clinic.title !== 'string' ||
      !clinic.title.trim() ||
      clinic.is_temporarily_closed === true ||
      clinic.is_permanently_closed === true
    ) {
      continue;
    }

    const phone = clinicText(clinic.phone);
    const phoneDigits = phone.replace(/\D/g, '');
    clinics.push({
      ...location,
      id: clinic.id,
      name: clinic.title.trim(),
      address: typeof clinic.address === 'string' ? clinic.address : '',
      city: typeof clinic.city === 'string' ? clinic.city : '',
      imageUrl: evidensiaUrl(clinic.image, 'evidensia.se', 'https://evidensia.se'),
      url,
      bookingUrl:
        clinic.online_booking === true
          ? evidensiaUrl(clinic.button_link, 'app.evidensia.se')
          : null,
      phone:
        /^\+?[\d\s()–-]+$/.test(phone) && phoneDigits.length >= 7 && phoneDigits.length <= 15
          ? phone
          : null,
      phoneInfo: clinicText(clinic.phone_info) || null,
      openingHours: parseOpeningHours(clinic, now),
    });
  }

  return clinics;
}

export function getNearestEvidensiaClinic(clinics: EvidensiaClinic[], location: unknown) {
  return getNearbyEvidensiaClinics(clinics, location, 1).find(hasEvidensiaClinicLocation) ?? null;
}

export function hasEvidensiaClinicLocation(clinic: EvidensiaClinic) {
  // Evidensia uses 0,0 for clinics without map coordinates.
  return (
    parseApproximateLocation(clinic) !== null && (clinic.latitude !== 0 || clinic.longitude !== 0)
  );
}

export function getNearbyEvidensiaClinics(
  clinics: EvidensiaClinic[],
  location: unknown,
  limit = 3,
) {
  const coordinate = parseApproximateLocation(location);
  if (!coordinate) return [];

  const radians = (degrees: number) => (degrees * Math.PI) / 180;

  return clinics
    .map((clinic) => {
      if (!hasEvidensiaClinicLocation(clinic)) return { ...clinic, distanceInKm: undefined };
      const latitudeDelta = radians(clinic.latitude - coordinate.latitude);
      const longitudeDelta = radians(clinic.longitude - coordinate.longitude);
      const haversine =
        Math.sin(latitudeDelta / 2) ** 2 +
        Math.cos(radians(coordinate.latitude)) *
          Math.cos(radians(clinic.latitude)) *
          Math.sin(longitudeDelta / 2) ** 2;
      const distanceInKm = 6371 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, haversine))));

      return { ...clinic, distanceInKm };
    })
    .sort((a, b) => (a.distanceInKm ?? Infinity) - (b.distanceInKm ?? Infinity))
    .slice(0, Math.max(0, limit));
}

export function findEvidensiaClinics(clinics: EvidensiaClinic[], query: string) {
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const search = normalize(query.trim());
  if (!search) return [];

  return clinics.filter((clinic) =>
    normalize(`${clinic.name} ${clinic.city} ${clinic.address}`).includes(search),
  );
}

export async function fetchEvidensiaClinics({
  fetchFn = fetch,
  timeoutInMs = 4000,
  treatment = 'id-marking',
}: {
  fetchFn?: typeof fetch;
  timeoutInMs?: number;
  treatment?: EvidensiaTreatment;
} = {}): Promise<EvidensiaClinic[] | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutInMs);

  try {
    const response = await fetchFn(EVIDENSIA_TREATMENTS[treatment].clinicsUrl, {
      signal: controller.signal,
    });
    return response.ok ? parseEvidensiaClinics(await response.json()) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
